/**
 * Copyright (c) 2024, Ouster, Inc.
 * All rights reserved.
 */
import {
  action,
  thunk,
  Action as EzPzAction,
  Thunk as EzPzThunk,
} from 'easy-peasy';

export type State = {
  isOpen: boolean;
  isLoading: boolean;
  onConfirm: () => Promise<void>;
  title?: string;
  body?: string;
  bullets?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  isDestructive?: boolean;
};

export const defaultState: State = {
  isOpen: false,
  isLoading: false,
  onConfirm: async () => {},
  title: undefined,
  body: undefined,
  bullets: undefined,
  confirmLabel: undefined,
  cancelLabel: undefined,
  isDestructive: undefined,
};

export interface ConfirmDialogModel extends State {
  openConfirmDialog: EzPzThunk<
    ConfirmDialogModel,
    {
      onConfirm: () => Promise<void>;
      title?: string;
      body?: string;
      bullets?: string[];
      confirmLabel?: string;
      cancelLabel?: string;
      isDestructive?: boolean;
    }
  >;
  closeConfirmDialog: EzPzAction<ConfirmDialogModel>;
  setIsOpen: EzPzAction<ConfirmDialogModel, boolean>;
  setIsLoading: EzPzAction<ConfirmDialogModel, boolean>;
  setOnConfirm: EzPzAction<ConfirmDialogModel, () => Promise<void>>;
  setTitle: EzPzAction<ConfirmDialogModel, string | undefined>;
  setBody: EzPzAction<ConfirmDialogModel, string | undefined>;
  setBullets: EzPzAction<ConfirmDialogModel, string[] | undefined>;
  setConfirmLabel: EzPzAction<ConfirmDialogModel, string | undefined>;
  setCancelLabel: EzPzAction<ConfirmDialogModel, string | undefined>;
  setIsDestructive: EzPzAction<ConfirmDialogModel, boolean | undefined>;
}

export const modal: ConfirmDialogModel = {
  ...defaultState,
  openConfirmDialog: thunk((actions, payload) => {
    actions.setOnConfirm(payload.onConfirm);
    actions.setTitle(payload.title);
    actions.setBody(payload.body);
    actions.setConfirmLabel(payload.confirmLabel);
    actions.setCancelLabel(payload.cancelLabel);
    actions.setIsDestructive(payload.isDestructive);
    actions.setBullets(payload.bullets);
    actions.setIsOpen(true);
  }),
  closeConfirmDialog: action((state) => {
    state.isOpen = false;
    state.isLoading = false;
    state.title = undefined;
    state.body = undefined;
    state.bullets = undefined;
    state.confirmLabel = undefined;
    state.cancelLabel = undefined;
    state.isDestructive = undefined;
  }),
  setIsOpen: action((state, isOpen) => {
    state.isOpen = isOpen;
  }),
  setTitle: action((state, title) => {
    state.title = title;
  }),
  setBody: action((state, body) => {
    state.body = body;
  }),
  setBullets: action((state, bullets) => {
    state.bullets = bullets;
  }),
  setConfirmLabel: action((state, confirmLabel) => {
    state.confirmLabel = confirmLabel;
  }),
  setCancelLabel: action((state, cancelLabel) => {
    state.cancelLabel = cancelLabel;
  }),
  setIsDestructive: action((state, isDestructive) => {
    state.isDestructive = isDestructive;
  }),
  setIsLoading: action((state, isLoading) => {
    state.isLoading = isLoading;
  }),
  setOnConfirm: action((state, onConfirm) => {
    state.onConfirm = onConfirm;
  }),
};
